import React from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { LinkButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import ArrowUpRightIcon from "@atlaskit/icon/core/arrow-up-right";
import { Box, Flex, Inline, Stack, xcss } from "@atlaskit/primitives-next";

import { SubjectAttributeKey } from "@/components/fastshift/analytics/constants";
import { DROPDOWN_ORG_ID } from "@/components/fastshift/src/state";
import { NO_ORG_ID } from "@/components/fastshift/src/state";

import { EmptySummary } from "../../../../common/empty-summary";
import { useFastshiftSelector } from "../../../../state";
import { useOrg, useSummary, useDashboardUIAnalytics } from "../hooks";
import { OrgSelectorDropdown } from "../org-selector-dropdown";

import { messages } from "./messages";
import { NoOrgDisplay } from "./no-org-display";
import { OtherOrgsDisplay } from "./other-orgs-display";
import { SummarySectionCard } from "./summary-section-card";

const topItemsStyles = xcss({
    height: "32px" // This prevents layout shift when buttons are hidden
});

const SummarySectionDetails: React.FC = () => {
    const intl = useIntl();
    const { domainOrg } = useOrg();
    const { summaryData, errors, isLoading, isEmpty } = useSummary();

    const isNoOrgSelected =
        domainOrg && domainOrg.orgId === DROPDOWN_ORG_ID.NO_ORG;
    const isOtherOrgsSelected =
        domainOrg && domainOrg.orgId === DROPDOWN_ORG_ID.OTHER_ORGS;

    // Show empty state if "Other orgs" is selected
    if (isNoOrgSelected) {
        return <NoOrgDisplay />;
    }

    if (isOtherOrgsSelected) {
        return <OtherOrgsDisplay />;
    }

    if (isEmpty) {
        return <EmptySummary orgId={domainOrg?.orgId || ""} />;
    }

    return (
        <>
            <Inline space="space.200">
                <SummarySectionCard
                    title={intl.formatMessage(messages.dataCenterCardHeading)}
                    value={summaryData.dataCenter.count}
                    isLoading={isLoading}
                    error={errors.l1Assessments}
                />
                <SummarySectionCard
                    title={intl.formatMessage(
                        messages.areaImprovementCardHeading
                    )}
                    value={summaryData.areaImprovement.count}
                    isLoading={isLoading}
                    error={errors.l1Assessments}
                />
                <SummarySectionCard
                    title={intl.formatMessage(messages.deletedAppsCardHeading)}
                    value={summaryData.deletedApps.count}
                    isLoading={isLoading}
                    error={errors.instances}
                />
            </Inline>
            <Inline space="space.200">
                <SummarySectionCard
                    title={intl.formatMessage(
                        messages.migrationCompletedCardHeading
                    )}
                    value={summaryData.migrationCompleted.count}
                    isLoading={isLoading}
                    error={errors.migrationPlanTotals}
                />
                <SummarySectionCard
                    title={intl.formatMessage(
                        messages.migrationInProgressCardHeading
                    )}
                    value={summaryData.migrationInProgress.count}
                    isLoading={isLoading}
                    error={errors.migrationPlanTotals}
                />
                <SummarySectionCard
                    title={intl.formatMessage(
                        messages.unsuccessfulMigrationCardHeading
                    )}
                    value={summaryData.unsuccessfulMigration.count}
                    isLoading={isLoading}
                    error={errors.migrationPlanTotals}
                />
            </Inline>
        </>
    );
};

export const SummarySection: React.FC = () => {
    const intl = useIntl();
    const customerDomain = useFastshiftSelector(
        (state) => state.customerDomain
    );
    const { domainOrg, setDomainOrg } = useOrg();
    const { sendMigrationSummarySectionLinkClickUIEvent } =
        useDashboardUIAnalytics();

    const baseUrl =
        process.env["NODE_ENV"] === "production"
            ? "https://admin.atlassian.com"
            : "https://admin.stg.atlassian.com";

    const migrationPlansUrl = `${baseUrl}/cloud-first-create-a-migration/${domainOrg?.orgId}`;
    const assessmentsUrl = `${baseUrl}/o/${domainOrg?.orgId}/product-insights/products`;

    // the selected org is not a special option (No org or Other orgs)
    const isValidCloudOrg =
        domainOrg &&
        !Object.values(DROPDOWN_ORG_ID).includes(
            domainOrg.orgId as DROPDOWN_ORG_ID
        );

    return (
        <Box as="section">
            <Stack space="space.100">
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    xcss={topItemsStyles}
                >
                    <Inline space="space.200" alignBlock="center">
                        <Heading size="small">
                            <FormattedMessage
                                {...messages.summarySectionTitle}
                            />
                        </Heading>
                        <OrgSelectorDropdown
                            customerDomain={customerDomain}
                            selectedDomainOrg={domainOrg}
                            onSetSelectedDomainOrg={setDomainOrg}
                        />
                    </Inline>
                    {isValidCloudOrg && (
                        <Inline space="space.100">
                            <LinkButton
                                href={assessmentsUrl}
                                iconAfter={ArrowUpRightIcon}
                                target="_blank"
                                onClick={() =>
                                    sendMigrationSummarySectionLinkClickUIEvent(
                                        SubjectAttributeKey.VIEW_ASSESSMENTS,
                                        domainOrg?.orgId || NO_ORG_ID
                                    )
                                }
                            >
                                {intl.formatMessage(messages.viewAssessments)}
                            </LinkButton>
                            <LinkButton
                                href={migrationPlansUrl}
                                iconAfter={ArrowUpRightIcon}
                                target="_blank"
                                onClick={() =>
                                    sendMigrationSummarySectionLinkClickUIEvent(
                                        SubjectAttributeKey.VIEW_MIGRATIONS,
                                        domainOrg?.orgId || NO_ORG_ID
                                    )
                                }
                            >
                                {intl.formatMessage(messages.viewMigrations)}
                            </LinkButton>
                        </Inline>
                    )}
                </Flex>
                <SummarySectionDetails />
            </Stack>
        </Box>
    );
};
